import * as React from 'react';
import {
  Typography,
  IconButton,
  Box,
  Pagination,
  CircularProgress,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  Stack,
} from '@mui/material';
import { connect } from "react-redux";
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import { centered_flex_box } from '../../app/components/Styles';
import {
  marketingColors,
  sectionLabelSx,
  sectionTitleSx,
  marketingFonts,
} from '../../app/components/tickitMarketingTheme';
import { MarketingPage, MarketingCard, MarketingButton } from '../../app/components/marketing';
import { motion } from 'framer-motion';
import { RequestCard } from "../../app/components";
import { getTicketRequests } from "../../app/store/actions/userActions";

const statusSelectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  color: marketingColors.gray900,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: marketingColors.gray200 },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: marketingColors.p700 },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: marketingColors.p700,
    borderWidth: "2px",
  },
  "& .MuiSelect-select": {
    color: marketingColors.gray900,
    fontFamily: marketingFonts.body,
  },
};

export const Requests = ({ requests, getTicketRequests, isLoading }) => {
  const [action, setAction] = React.useState(false);

  const initialState = {
    status: "Any",
    search: "",
    page: 1,
    limit: 10,
  };

  React.useEffect(() => {
    getTicketRequests(initialState);
  }, [action]);

  const [formData, setFormData] = React.useState(initialState);

  const handleClearFilter = () => {
    setFormData(initialState);
    getTicketRequests(initialState);
  };

  const { search, page } = formData;

  const handlePageChange = (event, value) => {
    setFormData({ ...formData, page: value });
    getTicketRequests({ ...formData, page: value });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearchFilter = () => {
    getTicketRequests(formData);
  };

  if (isLoading) {
    return (
      <MarketingPage>
        <Box sx={{ ...centered_flex_box, py: 8 }}>
          <CircularProgress sx={{ color: marketingColors.p700 }} />
        </Box>
      </MarketingPage>
    );
  }

  return (
    <MarketingPage maxWidth="xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography sx={sectionLabelSx}>Account</Typography>
          <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '1.75rem', md: '2.15rem' } }}>
            My requests
          </Typography>
        </Box>

        <MarketingCard sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  name="status"
                  onChange={handleChange}
                  label="Status"
                  sx={statusSelectSx}
                >
                  <MenuItem value="Accepted">Accepted</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                  <MenuItem value="Unseen">Pending</MenuItem>
                  <MenuItem value="Purchased">Purchased</MenuItem>
                  <MenuItem value="Any">Any</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={9}>
              <FormControl fullWidth>
                <OutlinedInput
                  id="search"
                  name="search"
                  value={search}
                  onChange={handleChange}
                  placeholder="Search for event requests"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearchFilter} edge="end">
                        <SearchIcon sx={{ color: marketingColors.p700 }} />
                      </IconButton>
                    </InputAdornment>
                  }
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    fontFamily: marketingFonts.body,
                    '& .MuiInputBase-input': { color: marketingColors.gray900 },
                    '& fieldset': { borderColor: marketingColors.gray200 },
                    '&:hover fieldset': { borderColor: marketingColors.p700 },
                    '&.Mui-focused fieldset': { borderColor: marketingColors.p700, borderWidth: '2px' },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <MarketingButton variant="outline" onClick={handleClearFilter} fullWidth sx={{ maxWidth: { sm: 'auto' } }}>
                  Clear Filters
                </MarketingButton>
                <MarketingButton onClick={handleSearchFilter} fullWidth sx={{ maxWidth: { sm: 'auto' } }}>
                  Apply Filters
                </MarketingButton>
              </Stack>
            </Grid>
          </Grid>
        </MarketingCard>

        <MarketingCard>
          {!requests?.isLoading ? (
            <Grid container spacing={3} sx={{ display: 'flex', justifyContent: 'center' }}>
              {requests?.results?.docs?.map((request) => (
                <Grid item xs={12} sm={6} md={4} key={request._id} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <RequestCard request={request} action={action} setAction={setAction} />
                </Grid>
              ))}
              {requests?.results?.docs?.length === 0 && (
                <Box sx={{ ...centered_flex_box, flexDirection: "column", py: 4, width: '100%' }}>
                  <InboxIcon sx={{ fontSize: 48, color: marketingColors.gray400, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: marketingColors.gray600, fontFamily: marketingFonts.body }}>
                    No requests found
                  </Typography>
                </Box>
              )}
            </Grid>
          ) : (
            <Box sx={{ ...centered_flex_box, py: 4 }}>
              <CircularProgress sx={{ color: marketingColors.p700 }} />
            </Box>
          )}
          <Box sx={{ ...centered_flex_box, mt: 3 }}>
            <Pagination
              count={requests?.results?.pages || 1}
              page={page}
              onChange={handlePageChange}
              color="primary"
              sx={{ '& .MuiPaginationItem-root': { borderRadius: 1 } }}
            />
          </Box>
        </MarketingCard>
      </motion.div>
    </MarketingPage>
  );
};

const mapStateToProps = (state) => ({
  requests: state?.records,
  isLoading: state?.records.isLoading,
});

const mapDispatchToProps = { getTicketRequests };

export default connect(mapStateToProps, mapDispatchToProps)(Requests);
